import {
  Alert,
  Checkbox,
  Divider,
  DropdownSelect,
  FieldSet,
  FormField,
  FormHelp,
  Grid,
  Heading,
  Label,
  LabeledControl,
  Legend,
  Paragraph,
  Radio,
  Text,
} from '@zillow/constellation';
// TODO: Replace with alternative CurrencyInput component
// import { CurrencyInput } from '@zillow/zrs-comet';
import { Controller, useFormContext } from 'react-hook-form';
import { FIELD_IDS } from '../../../pages/TenantDrivenLeaseRenewal';
import { useLeaseWebApiHandler_getLease } from '@zg-rentals/ts-leasing-client-react-query';
import { formatCentsToCurrency } from '../../../utils/humanReadable';
import { useLeasingUrlParams } from '../../../hooks/useLeasingUrlParams';

export enum PROPOSED_RENT_AMOUNT_TYPE {
  NO_PRICE_CHANGE = 'no-price-change',
  FIRST_MONTH_15_PERCENT_OFF = 'first-month-15-percent-off',
  CUSTOM = 'custom',
}

export function WhatDoYouWantToIncludeInYourRenewal() {
  const { leaseId } = useLeasingUrlParams();
  const { watch, setValue, clearErrors } = useFormContext();
  const { data } = useLeaseWebApiHandler_getLease({ leaseId });
  const monthlyRent = data?.lease?.costs?.monthlyRent || 0;
  const monthlyRent15PercentOff = Math.round(monthlyRent * 0.85) || 0;
  const formattedMonthlyRent = formatCentsToCurrency(monthlyRent);
  const formattedMonthlyRent15PercentOff = formatCentsToCurrency(monthlyRent15PercentOff);

  const selectedRequestsCount = [
    watch(FIELD_IDS.WAIVE_LATE_FEES_CHECKED),
    watch(FIELD_IDS.UPGRADE_APPLIANCE_CHECKED),
    watch(FIELD_IDS.PROFESSIONAL_CLEANING_CHECKED),
    watch(FIELD_IDS.YARD_MAINTENANCE_CHECKED),
  ].filter(Boolean).length;

  return (
    <Grid display="grid" gridTemplateColumns="repeat(12, 1fr)" gridColumn="1 / -1">
      <Grid gridColumnEnd={{ sm_lte: 'span 12', md: 'span 8', xl: 'span 6' }}>
        <Heading level={3} as="h2" marginBottom="sm" fontFamily="serif">
          What do you want to include in your renewal?
        </Heading>
        <Paragraph marginBottom="md" color="text.secondary">
          These suggestions are what landlords told us they’re most open to when renewing.
        </Paragraph>
        <Controller
          name={FIELD_IDS.RENT_TYPE}
          render={({ field: { value, onChange } }) => {
            const handleChange = (selectedValue: PROPOSED_RENT_AMOUNT_TYPE) => {
              onChange(selectedValue);
              switch (selectedValue) {
                case PROPOSED_RENT_AMOUNT_TYPE.NO_PRICE_CHANGE:
                  setValue(FIELD_IDS.RENT_AMOUNT, monthlyRent);
                  break;
                // We are sending the full rent amount because the backend will apply the 15% discount to the first month’s rent
                case PROPOSED_RENT_AMOUNT_TYPE.FIRST_MONTH_15_PERCENT_OFF:
                  setValue(FIELD_IDS.RENT_AMOUNT, monthlyRent);
                  break;
                case PROPOSED_RENT_AMOUNT_TYPE.CUSTOM:
                  setValue(FIELD_IDS.RENT_AMOUNT, null);
                  clearErrors(FIELD_IDS.RENT_AMOUNT);
                  break;
              }
            };

            return (
              <FieldSet required legend={<Legend>Rent amount</Legend>}>
                <LabeledControl
                  controlId="no-rent-increase"
                  label={
                    <Label>
                      No rent increase: {formattedMonthlyRent}/mo
                      <br />
                      <Text fontColor="textMedium">Most likely to be accepted</Text>
                    </Label>
                  }
                  control={
                    <Radio
                      name="rent-type"
                      value={PROPOSED_RENT_AMOUNT_TYPE.NO_PRICE_CHANGE}
                      checked={value === PROPOSED_RENT_AMOUNT_TYPE.NO_PRICE_CHANGE}
                      onChange={() => handleChange(PROPOSED_RENT_AMOUNT_TYPE.NO_PRICE_CHANGE)}
                    />
                  }
                />
                <LabeledControl
                  controlId="percentage-off"
                  label={
                    <Label>
                      15% off of one month’s rent: {formattedMonthlyRent15PercentOff}
                      <br />
                      <Text fontColor="textMedium">No increase for remaining months: {formattedMonthlyRent}/mo</Text>
                    </Label>
                  }
                  control={
                    <Radio
                      name="rent-type"
                      value={PROPOSED_RENT_AMOUNT_TYPE.FIRST_MONTH_15_PERCENT_OFF}
                      checked={value === PROPOSED_RENT_AMOUNT_TYPE.FIRST_MONTH_15_PERCENT_OFF}
                      onChange={() => handleChange(PROPOSED_RENT_AMOUNT_TYPE.FIRST_MONTH_15_PERCENT_OFF)}
                    />
                  }
                />
                <LabeledControl
                  controlId="other"
                  label={<Label>Other monthly rent amount</Label>}
                  control={
                    <Radio
                      name="rent-type"
                      value={PROPOSED_RENT_AMOUNT_TYPE.CUSTOM}
                      checked={value === PROPOSED_RENT_AMOUNT_TYPE.CUSTOM}
                      onChange={() => handleChange(PROPOSED_RENT_AMOUNT_TYPE.CUSTOM)}
                    />
                  }
                />
              </FieldSet>
            );
          }}
        />
        {/*watch(FIELD_IDS.RENT_TYPE) === PROPOSED_RENT_AMOUNT_TYPE.CUSTOM && (
          <Controller
            name={FIELD_IDS.RENT_AMOUNT}
            rules={{
              validate: (value) => {
                if (Number(value.replace(/,/g, '') > 1000000)) return 'Monthly rent cannot be greater than $1,000,000';
                return true;
              },
            }}
            render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => {
              return (
                <FormField
                  marginTop="sm"
                  required
                  label={<Label>Monthly rent</Label>}
                  control={
                    <AdornedInput
                      leftAdornment={<Adornment>$</Adornment>}
                      input={<CurrencyInput value={value} onChange={onChange} onBlur={onBlur} />}
                      error={!!error?.message}
                    />
                  }
                  description={error && <FormHelp error>{error.message}</FormHelp>}
                />
              );
            }}
          />
        )*/}
        <Divider marginY="md" />
        <FieldSet
          legend={<Legend>Additional requests</Legend>}
          description={<FormHelp>We recommend only choosing one or two</FormHelp>}
          optional
        >
          <Controller
            name={FIELD_IDS.WAIVE_LATE_FEES_CHECKED}
            render={({ field: { value, onChange } }) => (
              <LabeledControl
                label={<Label>Waive late fees within a grace period</Label>}
                control={<Checkbox checked={!!value} onChange={(e) => onChange(e.target.checked)} />}
              />
            )}
          />
          <Controller
            name={FIELD_IDS.UPGRADE_APPLIANCE_CHECKED}
            render={({ field: { value, onChange } }) => (
              <LabeledControl
                label={<Label>Upgrade an appliance</Label>}
                control={<Checkbox checked={!!value} onChange={(e) => onChange(e.target.checked)} />}
              />
            )}
          />
          <Controller
            name={FIELD_IDS.PROFESSIONAL_CLEANING_CHECKED}
            render={({ field: { value, onChange } }) => (
              <LabeledControl
                label={<Label>Provide one professional cleaning</Label>}
                control={<Checkbox checked={!!value} onChange={onChange} />}
              />
            )}
          />
          <Controller
            name={FIELD_IDS.YARD_MAINTENANCE_CHECKED}
            render={({ field: { value, onChange } }) => (
              <LabeledControl
                label={<Label>Provide yard maintenance</Label>}
                control={<Checkbox checked={!!value} onChange={onChange} />}
              />
            )}
          />
        </FieldSet>
        {selectedRequestsCount >= 3 && (
          <Alert
            marginTop="md"
            appearance="warning"
            body="Your landlord may not be able to accommodate multiple requests. Consider limiting to two or fewer."
          />
        )}
        {watch(FIELD_IDS.WAIVE_LATE_FEES_CHECKED) && (
          <Controller
            name={FIELD_IDS.WAIVE_LATE_FEES}
            render={({ field: { value, onChange } }) => (
              <FormField
                marginTop="sm"
                required
                label={<Label>Grace period</Label>}
                control={
                  <DropdownSelect
                    options={[
                      { value: '3', label: '3 days' },
                      { value: '5', label: '5 days' },
                      { value: '7', label: '7 days' },
                    ]}
                    showLabelForValue
                    value={value ?? ''}
                    onChange={onChange}
                  />
                }
              />
            )}
          />
        )}
        {watch(FIELD_IDS.UPGRADE_APPLIANCE_CHECKED) && (
          <Controller
            name={FIELD_IDS.UPGRADE_APPLIANCE}
            render={({ field: { value, onChange } }) => (
              <FormField
                marginTop="sm"
                required
                label={<Label>Appliance type</Label>}
                control={
                  <DropdownSelect
                    options={[
                      'Refrigerator',
                      'Oven/range',
                      'Microwave',
                      'Dishwasher',
                      'Clothes washer',
                      'Clothes dryer',
                      'Window air conditioners',
                    ]}
                    showLabelForValue
                    value={value ?? ''}
                    onChange={onChange}
                  />
                }
              />
            )}
          />
        )}
        {watch(FIELD_IDS.YARD_MAINTENANCE_CHECKED) && (
          <Controller
            name={FIELD_IDS.YARD_MAINTENANCE}
            render={({ field: { value, onChange } }) => (
              <FormField
                marginTop="sm"
                required
                label={<Label>Maintenance type</Label>}
                control={
                  <DropdownSelect options={['Landscaping', 'Snow removal']} value={value ?? ''} onChange={onChange} />
                }
              />
            )}
          />
        )}
      </Grid>
    </Grid>
  );
}
